import { useEffect, useState } from "react";
import { AdminShell } from "@/components/Layout";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import { Buildings, Plus, EnvelopeSimple, Trash, Copy, PencilSimple, ArrowsClockwise, X, User, House, Warning, ArrowRight, LinkSimple, Sparkle } from "@phosphor-icons/react";
import PMRModal from "@/components/PMRModal";
import LinkPropertiesModal from "@/components/LinkPropertiesModal";
import DraftUpdateModal from "@/components/DraftUpdateModal";
import LogoColorPicker from "@/components/LogoColorPicker";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminLandlords() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [landlords, setLandlords] = useState([]);
  const [propertyManagers, setPropertyManagers] = useState([]);
  const [pmr, setPmr] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingChain, setEditingChain] = useState(null);
  const [linkingLandlord, setLinkingLandlord] = useState(null);
  const [draftingFor, setDraftingFor] = useState(null); // landlord being drafted
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [l, p, t, pms] = await Promise.all([
        api.get("/landlords"), api.get("/pmr"), api.get("/tenant"), api.get("/property-managers"),
      ]);
      setLandlords(l.data || []);
      setPmr(p.data);
      setTenant(t.data);
      setPropertyManagers(pms.data || []);
    } catch (e) { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  // Deep-link support: /admin/landlords?focus=<id> auto-opens the edit modal
  // for that landlord (used by PropertyDetailV2's owner chip). Runs after
  // the landlords load so we can look up the target by id.
  useEffect(() => {
    const focusId = searchParams.get("focus");
    if (!focusId || landlords.length === 0) return;
    const target = landlords.find(l => l.id === focusId);
    if (target) {
      setEditing(target);
      // Scroll their row into view so the modal + row context align.
      setTimeout(() => {
        const el = document.querySelector(`[data-testid="landlord-row-${focusId}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
      // Clear the param so a refresh doesn't re-open the modal.
      searchParams.delete("focus");
      setSearchParams(searchParams, { replace: true });
    }
  }, [landlords, searchParams, setSearchParams]);

  const copy = (t) => { navigator.clipboard.writeText(`${window.location.origin}/portal/${t}`); toast.success("Portal link copied"); };

  const del = async (ll) => {
    if (!window.confirm(`Delete landlord "${ll.name}"? Their portal link will stop working immediately. Any properties assigned to them will be unassigned.`)) return;
    try { await api.delete(`/landlords/${ll.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const invite = async (ll) => {
    try {
      const { data } = await api.post(`/landlords/${ll.id}/invite`);
      if (data.dry_run) {
        navigator.clipboard.writeText(data.portal_url);
        toast.success("Emails OFF — link copied to clipboard. Send it manually if you want.", { duration: 5000 });
      } else {
        toast.success(data.sent ? `Portal invite sent to ${data.to}` : `Portal link ready`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const regen = async (ll) => {
    if (!window.confirm(`Rotate ${ll.name}'s portal token? Their current link will stop working immediately.`)) return;
    try {
      await api.post(`/landlords/${ll.id}/regen-token`);
      toast.success("New token issued");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const invitePmr = async () => { /* removed — PMR moved to /admin/property-managers */ };
  void invitePmr; void pmr; void busy; void setBusy;

  return (
    <AdminShell>
      <div className="p-8 max-w-5xl">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black text-4xl tracking-tight">Client Portal</h1>
            <p className="mt-1 text-slate-500 font-medium">Give PMR + landlords a read-only view of their jobs, ETAs, photos, and chargebacks.</p>
          </div>
          <button onClick={() => setShowAdd(true)} data-testid="add-landlord-btn"
            className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase tracking-wider inline-flex items-center gap-2">
            <Plus weight="bold" size={16}/> Add Landlord
          </button>
        </div>

        {/* Email safety banner */}
        {!tenant?.client_portal_emails_enabled && (
          <div className="mt-4 bg-red-50 border-2 border-red-500 p-4 flex items-start gap-3" data-testid="portal-emails-off-banner">
            <Warning weight="fill" size={22} className="text-red-600 flex-shrink-0 mt-0.5"/>
            <div>
              <div className="font-black uppercase text-sm tracking-widest text-red-800">Portal Emails Are OFF</div>
              <p className="mt-1 text-xs text-red-900 font-medium">
                Nothing will be emailed to landlords or PMR until portal emails are enabled in the account settings.
                All "Invite" buttons will copy the portal link to your clipboard instead so you can share it manually only when ready.
              </p>
            </div>
          </div>
        )}

        {/* PMR Card — quick link into full Property Managers area */}
        <section className="mt-6 rugged-card p-6" data-testid="pmr-card">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
                <Buildings weight="bold" size={22}/> Property Management Companies ({propertyManagers.length})
              </h2>
              <p className="mt-1 text-sm text-slate-600 font-medium max-w-xl">
                The companies that route work to your crew. Each landlord below can be tagged to one — their Owner Authorization emails will use that PM's branding, contact name and reply-to.
              </p>
            </div>
            <Link to="/admin/property-managers" data-testid="manage-pms-link"
              className="tactile-btn h-10 px-4 bg-[#0f172a] text-white font-black uppercase tracking-widest text-xs inline-flex items-center gap-1.5 hover:bg-slate-800">
              Manage Property Mgrs <ArrowRight weight="bold" size={12}/>
            </Link>
          </div>
          {propertyManagers.length === 0 ? (
            <div className="mt-4 p-3 border-2 border-dashed border-amber-400 bg-amber-50 text-sm text-amber-900 font-medium">
              No property managers configured yet. Owner Authorization emails need at least one PM record for the branding + reply-to.
              <Link to="/admin/property-managers" className="ml-2 font-black uppercase tracking-widest text-xs text-amber-800 hover:text-amber-950">Add one now →</Link>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-2">
              {propertyManagers.map(pm => (
                <div key={pm.id} className="inline-flex items-center gap-2 border-2 border-slate-200 bg-white px-3 h-10">
                  {pm.logo_url ? (
                    <img src={pm.logo_url} alt="" className="h-6 w-6 object-contain"/>
                  ) : (
                    <span className="h-3 w-3" style={{ background: pm.brand_color || "#00C4CC" }}/>
                  )}
                  <span className="text-xs font-bold text-[#0f172a]">{pm.name}</span>
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">· {pm.landlord_count || 0} LL</span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Landlord list */}
        <section className="mt-8">
          <h2 className="font-display font-black text-xl uppercase flex items-center gap-2">
            <User weight="bold" size={22}/> Landlords ({landlords.length})
          </h2>
          {landlords.length === 0 && (
            <div className="mt-4 p-6 border-2 border-dashed border-slate-300 text-center text-slate-500 font-medium" data-testid="landlords-empty">
              No landlords yet. Add one and assign their properties from the Property Detail page.
            </div>
          )}
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {landlords.map(ll => (
              <div key={ll.id} className="rugged-card p-4" data-testid={`landlord-${ll.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-black text-lg truncate" data-testid={`landlord-name-${ll.id}`}>{ll.name}</div>
                    <div className="text-xs text-slate-600 truncate">{ll.email}</div>
                    {ll.phone && <div className="text-xs text-slate-500">{ll.phone}</div>}
                  </div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest">
                    <House size={11}/> {ll.property_count} {ll.property_count === 1 ? "door" : "doors"}
                  </span>
                </div>
                {ll.notes && <p className="mt-2 text-xs text-slate-600 line-clamp-2">{ll.notes}</p>}
                {ll.property_manager_name && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 h-6 bg-slate-100 border border-slate-300 text-[10px] font-black uppercase tracking-widest text-slate-700" data-testid={`landlord-pm-tag-${ll.id}`}>
                      <Buildings size={10}/> {ll.property_manager_name}
                    </span>
                    {ll.coordinator_name && (
                      <span className="inline-flex items-center gap-1 px-2 h-6 bg-[#00C4CC] text-white text-[10px] font-black uppercase tracking-widest" data-testid={`landlord-coord-tag-${ll.id}`}>
                        <User size={10}/> {ll.coordinator_name}{ll.coordinator_role ? ` · ${ll.coordinator_role}` : ""}
                      </span>
                    )}
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-2">
                  <button onClick={() => setLinkingLandlord(ll)} data-testid={`landlord-link-${ll.id}`}
                    className="h-8 px-2 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
                    <LinkSimple size={10}/> Link Properties
                  </button>
                  <button onClick={() => copy(ll.magic_token)} data-testid={`landlord-copy-${ll.id}`}
                    className="h-8 px-2 border-2 border-[#0f172a] font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 inline-flex items-center gap-1">
                    <Copy size={10}/> Link
                  </button>
                  <button onClick={() => invite(ll)} data-testid={`landlord-invite-${ll.id}`}
                    className="h-8 px-2 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-[10px] hover:opacity-90 inline-flex items-center gap-1">
                    <EnvelopeSimple size={10}/> Invite
                  </button>
                  <button onClick={() => setDraftingFor(ll)} data-testid={`landlord-ai-draft-${ll.id}`}
                    className="h-8 px-2 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
                    <Sparkle weight="fill" size={10}/> AI Update
                  </button>
                  <button onClick={() => setEditing(ll)} data-testid={`landlord-edit-${ll.id}`}
                    className="h-8 px-2 border-2 border-[#0f172a] font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 inline-flex items-center gap-1">
                    <PencilSimple size={10}/> Edit
                  </button>
                  <button onClick={() => regen(ll)} data-testid={`landlord-regen-${ll.id}`}
                    className="h-8 px-2 border-2 border-slate-400 text-slate-600 font-black uppercase tracking-widest text-[10px] hover:bg-slate-50 inline-flex items-center gap-1">
                    <ArrowsClockwise size={10}/> Rotate
                  </button>
                  <button onClick={() => setEditingChain(ll)} data-testid={`landlord-chain-${ll.id}`}
                    className="h-8 px-2 border-2 border-amber-500 text-amber-700 font-black uppercase tracking-widest text-[10px] hover:bg-amber-50 inline-flex items-center gap-1">
                    Chain {(ll.notification_chain || []).length ? `(${(ll.notification_chain || []).length})` : ""}
                  </button>
                  <button onClick={() => del(ll)} data-testid={`landlord-delete-${ll.id}`}
                    className="h-8 px-2 border-2 border-red-500 text-red-600 font-black uppercase tracking-widest text-[10px] hover:bg-red-50 inline-flex items-center gap-1 ml-auto">
                    <Trash size={10}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>

      {(showAdd || editing) && (
        <LandlordModal
          initial={editing}
          propertyManagers={propertyManagers}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); load(); }}
        />
      )}
      {editingChain && (
        <ChainModal landlord={editingChain}
          onClose={() => setEditingChain(null)}
          onSaved={() => { setEditingChain(null); load(); }}/>
      )}
      <LinkPropertiesModal
        open={!!linkingLandlord}
        landlord={linkingLandlord}
        onClose={() => setLinkingLandlord(null)}
        onLinked={load}
      />
      <DraftUpdateModal
        open={!!draftingFor}
        landlordId={draftingFor?.id}
        onClose={() => setDraftingFor(null)}
        onSent={() => setDraftingFor(null)}
      />
    </AdminShell>
  );
}

function LandlordModal({ initial, propertyManagers = [], onClose, onSaved }) {
  const [form, setForm] = useState(initial || {
    name: "", email: "", phone: "", notes: "", principal_name: "",
    property_manager_id: "", coordinator_id: "",
    coverage_rules: { pest_window_days: 60, pest_multi_family_override: true, pest_note: "" },
    brand_logo: "", brand_color: "",
  });
  // Ensure coverage_rules is always present when editing an older landlord
  const coverage = form.coverage_rules || { pest_window_days: 60, pest_multi_family_override: true, pest_note: "" };
  const [busy, setBusy] = useState(false);
  const isEdit = !!initial;

  // Build the coordinator list for the currently-selected PM.
  const selectedPM = propertyManagers.find(pm => pm.id === form.property_manager_id);
  const coordinators = selectedPM ? [
    { id: "primary", name: selectedPM.contact_name || "(no primary set)", role: selectedPM.role_title || "Maintenance Coordinator" },
    ...(selectedPM.contacts || []).map(c => ({ id: c.id, name: c.name, role: c.role })),
  ] : [];

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    // Email required on create; existing landlords may pre-date the email requirement,
    // so let branding edits + updates go through without one.
    if (!isEdit && !form.email.trim()) return toast.error("Email required for new landlords");
    setBusy(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone || "", notes: form.notes || "",
        principal_name: (form.principal_name || "").trim(),
        property_manager_id: form.property_manager_id || "",
        coordinator_id: form.property_manager_id ? (form.coordinator_id || "") : "",
        brand_logo: (form.brand_logo || "").trim(),
        brand_color: (form.brand_color || "").trim(),
        coverage_rules: {
          pest_window_days: Math.max(0, Math.min(365, parseInt(coverage.pest_window_days) || 0)),
          pest_multi_family_override: !!coverage.pest_multi_family_override,
          pest_note: (coverage.pest_note || "").slice(0, 500),
        },
      };
      if (isEdit) await api.patch(`/landlords/${initial.id}`, payload);
      else await api.post("/landlords", payload);
      toast.success(isEdit ? "Updated" : "Landlord added");
      onSaved();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="landlord-modal">
      <div className="bg-white max-w-md w-full rounded-2xl border-2 border-[#0f172a] shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-[#0f172a] flex-shrink-0">
          <h2 className="font-display font-black text-xl uppercase">{isEdit ? "Edit Landlord" : "Add Landlord"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={20}/></button>
        </div>
        <div className="p-6 space-y-3 overflow-y-auto">
          <FormRow label="Name" value={form.name} testid="landlord-name-input" onChange={v => setForm({...form, name: v})} />
          <FormRow label="Email" value={form.email} testid="landlord-email-input" onChange={v => setForm({...form, email: v})} type="email" />
          <FormRow label="Phone" value={form.phone} testid="landlord-phone-input" onChange={v => setForm({...form, phone: v})} />
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Principal / Owner Name <span className="text-slate-400 normal-case font-medium">(optional)</span></label>
            <input value={form.principal_name || ""} onChange={e => setForm({...form, principal_name: e.target.value})}
              data-testid="landlord-principal-input"
              placeholder="e.g. Irfan Raza"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            <div className="mt-1 text-[10px] text-slate-500 font-medium">
              Group multiple LLCs under one person. Any insurance policy on a landlord with the same principal auto-applies here too (Irfan owns Raza Homes + AAR Holdings → one AWR policy covers both).
            </div>
          </div>
          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Managing Company (tag)</label>
            <select value={form.property_manager_id || ""} onChange={e => setForm({...form, property_manager_id: e.target.value, coordinator_id: ""})}
              data-testid="landlord-pm-select"
              className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm bg-white">
              <option value="">— No tag (uses default company) —</option>
              {propertyManagers.map(pm => (
                <option key={pm.id} value={pm.id}>{pm.name}</option>
              ))}
            </select>
          </div>
          {form.property_manager_id && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Assigned Coordinator</label>
              <select value={form.coordinator_id || "primary"} onChange={e => setForm({...form, coordinator_id: e.target.value})}
                data-testid="landlord-coordinator-select"
                className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm bg-white">
                {coordinators.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.role ? ` · ${c.role}` : ""}{c.id === "primary" ? " (primary)" : ""}</option>
                ))}
              </select>
              <div className="mt-1 text-[10px] text-slate-500 font-medium">
                Owner Authorization emails to this landlord will be signed by this specific coordinator (name, role, phone, email).
              </div>
            </div>
          )}
          <FormRow label="Notes" value={form.notes} testid="landlord-notes-input" onChange={v => setForm({...form, notes: v})} multiline />

          <div className="mt-3 p-3 border-2 border-indigo-500 bg-indigo-50" data-testid="landlord-branding-block">
            <div className="text-[10px] font-black uppercase tracking-widest text-indigo-800 mb-2">
              🎨 Portal Branding — landlord-facing chrome
            </div>
            <div className="text-[11px] text-slate-600 mb-2">
              Overrides Field Radar's default navy header on this landlord's portal. Leave blank to inherit defaults.
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Logo</label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    value={form.brand_logo || ""}
                    onChange={e => setForm({...form, brand_logo: e.target.value})}
                    placeholder="Upload a file or paste a URL"
                    data-testid="landlord-brand-logo-input"
                    className="flex-1 h-11 px-3 border-2 border-[#0f172a] text-sm font-mono"/>
                  {isEdit && (
                    <>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        id={`landlord-logo-file-${initial.id}`}
                        data-testid="landlord-logo-file-input"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          const fd = new FormData();
                          fd.append("file", f);
                          try {
                            await api.post(`/landlords/${initial.id}/logo`, fd,
                              { headers: { "Content-Type": "multipart/form-data" } });
                            toast.success("Logo uploaded — tenants will see it on their portal");
                            onSaved();  // reload list + close modal
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
                          } finally {
                            e.target.value = "";
                          }
                        }}/>
                      <label
                        htmlFor={`landlord-logo-file-${initial.id}`}
                        data-testid="landlord-logo-upload-btn"
                        className="h-11 px-3 border-2 border-[#0f172a] bg-[#00C4CC] text-[#0f172a] font-black uppercase text-[10px] tracking-widest cursor-pointer hover:bg-cyan-300 flex items-center gap-1">
                        📤 Upload
                      </label>
                      {(form.brand_logo || "").trim() && (
                        <button
                          type="button"
                          data-testid="landlord-logo-remove-btn"
                          onClick={async () => {
                            if (!window.confirm("Remove this landlord's logo? Tenants will see the default header again.")) return;
                            try {
                              await api.delete(`/landlords/${initial.id}/logo`);
                              toast.success("Logo removed");
                              onSaved();
                            } catch (err) {
                              toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
                            }
                          }}
                          className="h-11 px-3 border-2 border-red-600 bg-white text-red-600 font-black uppercase text-[10px] tracking-widest hover:bg-red-50">
                          Remove
                        </button>
                      )}
                    </>
                  )}
                </div>
                {!isEdit && (
                  <div className="text-[10px] text-slate-500 mt-1">Save the landlord first to unlock file upload.</div>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">Color</label>
                <div className="mt-1 flex items-center gap-1 h-11 px-2 border-2 border-[#0f172a] bg-white">
                  <input
                    type="color"
                    value={/^#[0-9a-f]{6}$/i.test(form.brand_color || "") ? form.brand_color : "#0f172a"}
                    onChange={e => setForm({...form, brand_color: e.target.value})}
                    data-testid="landlord-brand-color-picker"
                    className="h-8 w-8 border border-slate-300 cursor-pointer"/>
                  <input
                    value={form.brand_color || ""}
                    onChange={e => setForm({...form, brand_color: e.target.value})}
                    placeholder="#0f172a"
                    data-testid="landlord-brand-color-input"
                    className="h-8 w-24 px-2 text-xs font-mono border border-slate-300"/>
                </div>
              </div>
              <div className="col-span-2">
                <LogoColorPicker
                  src={isEdit ? (
                    /^(https?:|data:|\/)/i.test(form.brand_logo || "")
                      ? form.brand_logo
                      : `${API}/public/brand-logo/landlord/${initial?.id || ""}?v=${initial?.brand_logo_updated_at || ""}`
                  ) : ""}
                  onPick={(c) => setForm({...form, brand_color: c})}/>
                <div className="mt-1 text-[10px] text-slate-500">
                  {isEdit ? "Extracts up to 6 dominant colors from the uploaded logo." : "Save the landlord + upload a logo first."}
                </div>
              </div>
            </div>
            {(form.brand_logo || form.brand_color) && (
              <div className="mt-3 flex items-center gap-3 p-2 text-white"
                style={{ background: /^#[0-9a-f]{6}$/i.test(form.brand_color || "") ? form.brand_color : "#0f172a" }}
                data-testid="landlord-brand-preview">
                {form.brand_logo && (
                  <img
                    src={/^(https?:|data:|\/)/i.test(form.brand_logo) ? form.brand_logo : `${API}/public/brand-logo/landlord/${initial?.id || ""}?v=${initial?.brand_logo_updated_at || ""}`}
                    alt="logo preview"
                    className="h-10 max-w-[180px] object-contain bg-white/95 border border-white/70 px-1 py-0.5"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                )}
                <div className="min-w-0">
                  <div className="text-[9px] font-black uppercase tracking-widest text-amber-400">Preview · Portfolio Portal</div>
                  <div className="font-display font-black text-sm truncate">{form.name || "Landlord Name"}</div>
                </div>
              </div>
            )}
          </div>

          <div className="mt-3 p-3 border-2 border-fuchsia-500 bg-fuchsia-50" data-testid="landlord-coverage-rules">
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-800 mb-2">
              🤖 Triage AI Coverage Rules — pest control
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label>
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-0.5">Pest Window (days)</div>
                <input type="number" min="0" max="365"
                  value={coverage.pest_window_days}
                  onChange={e => setForm({...form, coverage_rules: {...coverage, pest_window_days: e.target.value}})}
                  data-testid="landlord-pest-window"
                  className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm font-mono font-black"/>
                <div className="text-[10px] text-slate-500 mt-0.5">Days after move-in that pest control is covered</div>
              </label>
              <label className="flex flex-col justify-start">
                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-0.5">Multi-Family Override</div>
                <label className="inline-flex items-center gap-2 h-11 px-3 border-2 border-[#0f172a] cursor-pointer bg-white"
                  data-testid="landlord-mf-override">
                  <input type="checkbox"
                    checked={coverage.pest_multi_family_override}
                    onChange={e => setForm({...form, coverage_rules: {...coverage, pest_multi_family_override: e.target.checked}})}
                    className="w-4 h-4"/>
                  <span className="text-xs font-bold">Always covered if multi-family</span>
                </label>
                <div className="text-[10px] text-slate-500 mt-0.5">Duplex/apartment/condo overrides the window above</div>
              </label>
            </div>
            <div className="mt-2">
              <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-0.5">Extra note for AI (optional)</div>
              <input value={coverage.pest_note || ""}
                onChange={e => setForm({...form, coverage_rules: {...coverage, pest_note: e.target.value}})}
                placeholder="e.g. Only cover roaches + mice, never bed bugs" data-testid="landlord-pest-note"
                className="w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 border-t-2 border-slate-200 p-4 flex gap-2 bg-white">
          <button onClick={onClose} className="flex-1 h-11 border-2 border-[#0f172a] font-black uppercase tracking-widest text-xs">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="landlord-save-btn"
            className="flex-1 h-11 bg-[#00C4CC] text-white font-black uppercase tracking-widest text-xs disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PMRModalOldRemoved() { return null; }


function ChainModal({ landlord, onClose, onSaved }) {
  const [chain, setChain] = useState(landlord.notification_chain || []);
  const [busy, setBusy]   = useState(false);
  const [events, setEvents] = useState([]);
  const [testOpen, setTestOpen] = useState(false);
  const [testForm, setTestForm] = useState({ property_address: "", incident_summary: "" });
  const [testBusy, setTestBusy] = useState(false);
  const add = () => setChain([...chain, {
    role: "custom", name: "New Recipient", email: "", phone: "",
    sequence: (chain[chain.length-1]?.sequence || 0) + 1, delay_min: 60, channels: ["email"],
  }]);
  const patch = (i, k, v) => setChain(chain.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const remove = (i) => setChain(chain.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/landlords/${landlord.id}/notification-chain`, chain);
      toast.success(`Chain saved · ${chain.length} recipient${chain.length === 1 ? "" : "s"}`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Save failed"));
    } finally { setBusy(false); }
  };

  const loadEvents = async () => {
    try {
      const { data } = await api.get(`/notification-events`, { params: { landlord_id: landlord.id } });
      setEvents(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadEvents(); }, []); // eslint-disable-line

  const dispatch = async () => {
    if (!testForm.incident_summary.trim()) return toast.error("Add an incident summary");
    if (chain.length === 0) return toast.error("Save the chain first");
    setTestBusy(true);
    try {
      const { data } = await api.post(`/notification-chain/dispatch`, {
        landlord_id: landlord.id,
        property_address: testForm.property_address || landlord.name,
        incident_summary: testForm.incident_summary,
      });
      toast.success(`Chain dispatched · tier 1 sent · ${data.steps} step${data.steps === 1 ? "" : "s"}`);
      setTestOpen(false);
      setTestForm({ property_address: "", incident_summary: "" });
      loadEvents();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Dispatch failed"));
    } finally { setTestBusy(false); }
  };

  const resolveEvent = async (ev) => {
    if (!window.confirm("Mark this incident resolved? Any pending escalations will be cancelled.")) return;
    try {
      await api.post(`/notification-events/${ev.id}/resolve`);
      toast.success("Marked resolved");
      loadEvents();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Resolve failed")); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      onClick={onClose} data-testid="chain-modal">
      <div className="bg-white max-w-2xl w-full my-4" onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-[#0f172a] flex items-center justify-between">
          <div>
            <h2 className="font-display font-black text-xl uppercase tracking-tight">Notification Chain</h2>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">{landlord.name}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 bg-slate-200 hover:bg-slate-300 flex items-center justify-center"><X size={18}/></button>
        </div>
        <div className="p-4 space-y-3 max-h-[65vh] overflow-y-auto">
          {/* Live events panel — active + recent escalations for this landlord */}
          {events.length > 0 && (
            <div className="p-3 border-2 border-amber-500 bg-amber-50" data-testid="chain-events-panel">
              <div className="text-[10px] font-black uppercase tracking-widest text-amber-800 mb-2">
                Recent Escalations ({events.length})
              </div>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {events.slice(0, 5).map((ev) => (
                  <div key={ev.id} className="p-2 bg-white border border-amber-300 text-xs" data-testid={`event-row-${ev.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-black text-[#0f172a] truncate">{ev.property_address || "(no address)"}</div>
                        <div className="text-[11px] text-slate-600 line-clamp-2">{ev.incident_summary}</div>
                        <div className="mt-1 flex flex-wrap gap-1 text-[9px] font-black uppercase tracking-widest">
                          {ev.resolved_at ? (
                            <span className="px-1.5 py-0.5 bg-emerald-600 text-white">Resolved</span>
                          ) : ev.acknowledged_at ? (
                            <span className="px-1.5 py-0.5 bg-amber-500 text-white">Paused · Ack'd</span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-red-600 text-white">Active</span>
                          )}
                          <span className="px-1.5 py-0.5 bg-slate-200 text-slate-800">
                            {(ev.steps || []).filter(s => s.sent_at).length}/{(ev.steps || []).length} sent
                          </span>
                        </div>
                      </div>
                      {!ev.resolved_at && (
                        <button onClick={() => resolveEvent(ev)} data-testid={`event-resolve-${ev.id}`}
                          className="h-6 px-2 bg-emerald-600 text-white font-black uppercase tracking-widest text-[9px] hover:bg-emerald-700">
                          Close
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test dispatch inline card */}
          {testOpen && (
            <div className="p-3 border-2 border-fuchsia-500 bg-fuchsia-50 space-y-2" data-testid="chain-test-panel">
              <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-800">
                Dispatch Test Escalation
              </div>
              <input list="known-addresses" autoComplete="off" value={testForm.property_address}
                onChange={e => setTestForm({...testForm, property_address: e.target.value})}
                placeholder="Property address (e.g. 5346 Chew Ave, Unit 2)"
                data-testid="chain-test-address"
                className="w-full h-9 px-2 text-xs border-2 border-slate-300 font-mono"/>
              <textarea value={testForm.incident_summary}
                onChange={e => setTestForm({...testForm, incident_summary: e.target.value})}
                placeholder="Incident summary (e.g. Water leak under kitchen sink)"
                data-testid="chain-test-summary"
                rows={2}
                className="w-full px-2 py-1 text-xs border-2 border-slate-300 resize-none"/>
              <div className="flex gap-2">
                <button onClick={() => setTestOpen(false)}
                  className="h-8 px-2 border-2 border-slate-400 font-black uppercase text-[10px] tracking-widest">Cancel</button>
                <button onClick={dispatch} disabled={testBusy} data-testid="chain-test-fire"
                  className="h-8 px-3 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50">
                  {testBusy ? "Firing…" : "Fire Tier-1 Now"}
                </button>
              </div>
              <p className="text-[10px] text-slate-500">
                Tier-1 recipient(s) get the "I've got this" email immediately. Later tiers wait for their delay_min timer.
              </p>
            </div>
          )}

          {chain.length === 0 && <div className="text-center text-slate-400 py-6 text-sm">No recipients yet — tap Add to start.</div>}
          {chain.map((r, i) => (
            <div key={i} className="p-3 border-2 border-slate-300 bg-slate-50" data-testid={`chain-row-${i}`}>
              <div className="flex items-center gap-2 mb-2">
                <input type="number" value={r.sequence} min={1} max={10}
                  onChange={e => patch(i, "sequence", parseInt(e.target.value)||1)}
                  className="w-14 h-8 px-2 text-xs border-2 border-slate-300 text-center font-black"/>
                <input value={r.name} onChange={e => patch(i, "name", e.target.value)}
                  placeholder="Name" className="flex-1 h-8 px-2 text-sm border-2 border-slate-300"/>
                <button onClick={() => remove(i)} data-testid={`chain-remove-${i}`}
                  className="h-8 w-8 bg-red-100 text-red-700 border-2 border-red-500 flex items-center justify-center"><X size={12}/></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={r.role} onChange={e => patch(i, "role", e.target.value)}
                  placeholder="role (lessee_coordinator)" className="h-9 px-2 text-xs border border-slate-300 font-mono"/>
                <input type="number" value={r.delay_min} min={0} max={1440}
                  onChange={e => patch(i, "delay_min", parseInt(e.target.value)||0)}
                  placeholder="delay (min)" className="h-9 px-2 text-xs border border-slate-300"/>
                <input type="email" value={r.email} onChange={e => patch(i, "email", e.target.value)}
                  placeholder="email" className="h-9 px-2 text-xs border border-slate-300 font-mono col-span-2"/>
                <input value={r.phone} onChange={e => patch(i, "phone", e.target.value)}
                  placeholder="phone (optional)" className="h-9 px-2 text-xs border border-slate-300 col-span-2"/>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t-2 border-[#0f172a] flex flex-wrap gap-2">
          <button onClick={add} className="h-11 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-widest">+ Add Recipient</button>
          <button onClick={() => setTestOpen(o => !o)} data-testid="chain-test-toggle"
            className="h-11 px-3 border-2 border-fuchsia-600 text-fuchsia-700 font-black uppercase text-xs tracking-widest hover:bg-fuchsia-50">
            {testOpen ? "Hide Test" : "Test Dispatch"}
          </button>
          <div className="flex-1"/>
          <button onClick={onClose} className="h-11 px-4 border-2 border-slate-400 font-black uppercase text-xs tracking-widest">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="chain-save"
            className="h-11 px-4 bg-amber-500 hover:bg-amber-600 text-white font-black uppercase text-xs tracking-widest border-2 border-[#0f172a]">
            {busy ? "Saving…" : "Save Chain"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormRow({ label, value, onChange, type = "text", testid, multiline }) {
  return (
    <div>
      <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">{label}</label>
      {multiline ? (
        <textarea value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testid}
          rows={3} className="mt-1 w-full px-3 py-2 border-2 border-[#0f172a] text-sm resize-none"/>
      ) : (
        <input value={value || ""} onChange={e => onChange(e.target.value)} type={type} data-testid={testid}
          className="mt-1 w-full h-11 px-3 border-2 border-[#0f172a] text-sm"/>
      )}
    </div>
  );
}
